import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  apiWorker, 
  sendAdminNodeNotification, 
  getAdminSystemAlerts, 
  deleteAdminSystemAlert,
  getAdminBroadcastRecipients 
} from '../api/worker';
import { 
  AdminBroadcastParams, 
  DbOrphansScanResult, 
  DbOrphansCleanupResult, 
  StorageOrphansScanResult, 
  StorageOrphansCleanupResult,
  OrphanedNode,
  PurgeOrphanedNodesResult,
  DatabaseIndexSyncResult,
  ModuleBackfillResult
} from '../types';
import toast from 'react-hot-toast';

export const useAdminUsers = (page: number = 0, size: number = 20) => {
  return useQuery({
    queryKey: ['admin', 'users', page, size],
    queryFn: () => apiWorker.get(`/api/admin/users`, { params: { page, size } }).then(r => r.data),
  });
};

export const useAdminNodes = (page: number = 0, size: number = 20) => {
  return useQuery({
    queryKey: ['admin', 'nodes', page, size],
    queryFn: () => apiWorker.get(`/api/admin/nodes`, { params: { page, size } }).then(r => r.data),
  });
};

export const useAdminContactInquiries = (page: number = 0, size: number = 20) => {
  return useQuery({
    queryKey: ['admin', 'contact-inquiries', page, size],
    queryFn: () => apiWorker.get(`/api/admin/contact-inquiries`, { params: { page, size } }).then(r => r.data),
  });
};

export const useAdminSync = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiWorker.post(`/api/admin/sync`).then(r => r.data),
    onSuccess: (data: any) => {
      const summary = data?.indexesEnsured != null
        ? `Deep sync complete: ${data.indexesEnsured} indexes verified, ${data.nodesReconciled ?? 0} nodes reconciled.`
        : 'Deep sync completed successfully.';
      toast.success(summary);
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit'] });
    },
    onError: () => {
      toast.error('Failed to trigger deep sync');
    }
  });
};

export const useSyncClerkUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (clerkUserId: string) => {
      try {
        const res = await apiWorker.post(`/api/admin/users/${clerkUserId}/sync`);
        return res.data;
      } catch (err: any) {
        if (err?.response?.status === 404 || err?.response?.status === 405) {
          try {
            const fallback = await apiWorker.post('/api/admin/sync', { clerkId: clerkUserId });
            return fallback.data;
          } catch {
            const emptyFallback = await apiWorker.post('/api/admin/sync');
            return emptyFallback.data;
          }
        }
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('User identity synced from Clerk into database');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes', 'orphans'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to sync user from Clerk');
    }
  });
};

export const useAdminHealth = () => {
  return useQuery({
    queryKey: ['admin', 'health'],
    queryFn: () => apiWorker.get(`/api/health`).then(r => r.data),
    retry: false
  });
};

export const useAdminBroadcast = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, params }: { nodeId: string; params: AdminBroadcastParams }) =>
      sendAdminNodeNotification(nodeId, params),
    onSuccess: () => {
      toast.success('Admin notification broadcast dispatched successfully.');
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'system-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['my-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
    onError: () => {
      toast.error('Failed to broadcast admin notification');
    }
  });
};

export const useAdminSystemAlerts = (page = 0, size = 10) => {
  return useQuery({
    queryKey: ['admin', 'notifications', 'system-alerts', page, size],
    queryFn: () => getAdminSystemAlerts(page, size)
  });
};

export const useAdminBroadcasts = useAdminSystemAlerts;

export const useDeleteAdminSystemAlert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (alertId: string) => deleteAdminSystemAlert(alertId),
    onSuccess: () => {
      toast.success('Broadcast revoked and deleted successfully.');
      queryClient.invalidateQueries({ queryKey: ['admin', 'notifications', 'system-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['my-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-list'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
    onError: () => {
      toast.error('Failed to revoke broadcast');
    }
  });
};

export const useDeleteAdminBroadcast = useDeleteAdminSystemAlert;

export const useAdminBroadcastRecipients = (broadcastId: string | null, page = 0, size = 10) => {
  return useQuery({
    queryKey: ['admin', 'notifications', 'broadcast-recipients', broadcastId, page, size],
    queryFn: () => broadcastId ? getAdminBroadcastRecipients(broadcastId, page, size) : Promise.reject('No broadcastId'),
    enabled: !!broadcastId,
  });
};

export const useScanDbOrphans = () => {
  return useMutation({
    mutationFn: async (): Promise<DbOrphansScanResult> => {
      const res = await apiWorker.get('/api/admin/nodes/orphans/records');
      return res.data;
    }
  });
};

export const useScanOrphanedNodes = () => {
  return useQuery({
    queryKey: ['admin', 'nodes', 'orphans'],
    queryFn: async (): Promise<OrphanedNode[]> => {
      const res = await apiWorker.get<OrphanedNode[]>('/api/admin/nodes/orphans');
      return Array.isArray(res.data) ? res.data : [];
    }
  });
};

export const useReassignNodeOwner = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ nodeId, targetUserId }: { nodeId: string; targetUserId: string }) => {
      const res = await apiWorker.post(`/api/admin/nodes/${nodeId}/reassign-owner`, { targetUserId });
      return res.data;
    },
    onSuccess: (data: any) => {
      toast.success(`Owner successfully reassigned for ${data?.name || 'node'}`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes', 'orphans'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['directory'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to reassign node owner');
    }
  });
};

export const usePurgeUnownedOrphanedNodes = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PurgeOrphanedNodesResult> => {
      const res = await apiWorker.delete('/api/admin/nodes/orphans/unowned');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Purged ${data.purgedCount} unowned orphaned node(s).`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes', 'orphans'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to purge unowned orphaned nodes');
    }
  });
};

export const usePurgeDbOrphans = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<DbOrphansCleanupResult> => {
      const res = await apiWorker.post('/api/admin/nodes/orphans/cleanup');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Successfully purged ${data.totalPurgedRecords} orphaned database record(s).`);
      queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-mock'] });
      queryClient.invalidateQueries({ queryKey: ['memberships'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
    },
    onError: () => {
      toast.error('Failed to purge orphaned database records.');
    }
  });
};

export const useScanStorageOrphans = () => {
  return useMutation({
    mutationFn: async (): Promise<StorageOrphansScanResult> => {
      const res = await apiWorker.get('/api/admin/storage/orphans');
      return res.data;
    }
  });
};

export const usePurgeStorageOrphans = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<StorageOrphansCleanupResult> => {
      const res = await apiWorker.delete('/api/admin/storage/orphans');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Storage cleanup complete: ${data.deletedAssetsCount} file(s) removed, ${data.reconciledNodesCount} node quota(s) reconciled.`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['vault'] });
      queryClient.invalidateQueries({ queryKey: ['vault-assets'] });
    },
    onError: () => {
      toast.error('Failed to purge orphaned storage files.');
    }
  });
};

export const useSyncDatabaseIndexes = () => {
  return useMutation({
    mutationFn: async (): Promise<DatabaseIndexSyncResult> => {
      const res = await apiWorker.post('/api/admin/database/indexes/sync');
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(`Index sync complete: ${data.totalIndexesCreated} indexes verified across ${data.collectionsProcessed} collections.`);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to synchronize database indexes.');
    }
  });
};

export const useBackfillDefaultModules = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<ModuleBackfillResult> => {
      const res = await apiWorker.post<ModuleBackfillResult>('/api/admin/nodes/modules/backfill-defaults');
      return res.data;
    },
    onSuccess: (data: ModuleBackfillResult) => {
      toast.success(data?.message || `Default modules backfilled: ${data?.modifiedCount ?? 0} node(s) updated.`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'nodes'] });
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });
      queryClient.invalidateQueries({ queryKey: ['nodes'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to backfill default modules.');
    }
  });
};



